import { createSelector } from '@reduxjs/toolkit'

import { getColumnName } from 'components_new/organisms/sim/Node/utils'

import { ApplicationState } from 'redux/Stores/types'
import { CollectSchema, SourceTable } from 'types/GlobalDataset'
import { FieldSource } from 'types/transformation/types/fieldSources'
import { sortAlphabeticalAsc } from 'utils/sortHelper'

function resolveSourceName(
  sourceTableId: string,
  collect: CollectSchema
): string | null {
  for (const source of Object.values(collect)) {
    if (source.tables[sourceTableId]) {
      return source.label
    }
  }

  return null
}

export const selectSIM = createSelector(
  [(state: ApplicationState) => state.DataPlatformSIMStore.data],
  (data) => {
    if (!data) return null

    return {
      ...data,
      tables: [...data.tables].sort((a, b) =>
        sortAlphabeticalAsc(a.name, b.name)
      )
    }
  }
)

export const selectSIMStatus = createSelector(
  [(state: ApplicationState) => state.DataPlatformSIMStore.status],
  (status) => status
)

export const selectSIMWithSources = createSelector(
  [
    (state: ApplicationState) => state.DataPlatformSIMStore.data,
    (state: ApplicationState) => state.SimTableStore.sourceSchemas
  ],
  (data, collect) => {
    if (!data) return null

    return {
      ...data,
      tables: [...data.tables]
        .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
        .map((table) => ({
          ...table,
          source_names: collect
            ? (
                Array.from(
                  new Set(
                    table.source_tables
                      .map((id) => resolveSourceName(id, collect))
                      .filter(Boolean)
                  )
                ) as string[]
              ).sort((a, b) => sortAlphabeticalAsc(a, b))
            : []
        }))
    }
  }
)

// Source tables and per-field source columns as names, keyed by table
// technical name, then field real_name.
export type SIMFieldSources = Map<
  string,
  { tables: string[]; fields: { [realName: string]: string[] } }
>

const UNKNOWN_TABLE = 'Okänd källtabell'

// A broken reference on the way, listed so the field does not look sourced.
const MISSING_SOURCE = 'Saknas i flödet'

type SourceIndex = Map<string, { system: string; table: SourceTable }>

const indexSources = (collect: CollectSchema): SourceIndex => {
  const index: SourceIndex = new Map()

  Object.values(collect).forEach((source) => {
    Object.entries(source.tables).forEach(([id, table]) => {
      index.set(id, { system: source.label, table })
    })
  })

  return index
}

const sourceTableName = (id: string, index: SourceIndex): string => {
  const entry = index.get(id)

  return entry ? `${entry.system} › ${entry.table.label}` : UNKNOWN_TABLE
}

// INPUT column ids are derived from node id + technical name, so derive them
// again to find the column; one the source no longer has keeps its id part.
const sourceColumnName = (source: FieldSource, index: SourceIndex): string => {
  const table = source.source_table_id
    ? sourceTableName(source.source_table_id, index)
    : UNKNOWN_TABLE
  const column = (
    source.source_table_id ? index.get(source.source_table_id) : undefined
  )?.table.columns.find(
    (item) =>
      getColumnName(source.input_node_id, item.technical_name) ===
      source.column_id
  )

  if (!column) {
    const prefix = getColumnName(source.input_node_id, '')
    const technicalName = source.column_id.startsWith(prefix)
      ? source.column_id.slice(prefix.length)
      : source.column_id

    return `${table} › ${technicalName}`
  }

  const label =
    column.label === column.technical_name
      ? column.label
      : `${column.label} (${column.technical_name})`

  return `${table} › ${label}`
}

export const selectSIMFieldSources = createSelector(
  [
    (state: ApplicationState) => state.DataPlatformSIMStore.data,
    (state: ApplicationState) => state.SimTableStore.sourceSchemas
  ],
  (data, collect): SIMFieldSources => {
    const index = indexSources(collect)

    return new Map(
      (data?.tables ?? []).map((table) => [
        table.technical_name,
        {
          tables: table.source_tables.map((id) => sourceTableName(id, index)),
          fields: Object.fromEntries(
            Object.entries(table.field_sources).map(([realName, field]) => [
              realName,
              [
                ...field.sources.map((source) =>
                  sourceColumnName(source, index)
                ),
                ...(field.unresolved ? [MISSING_SOURCE] : [])
              ]
            ])
          )
        }
      ])
    )
  }
)
